import {
  CalculatedPlayer,
  PlayerRoundScore,
  Colours,
  RoundModifier,
} from '@alienheadwars/football-card-manager-model';
import { Stack, Card, Typography, Box } from '@mui/material';
import { localise } from '../../../../../locale/locale';
import { IMPACT_FONTS } from '../../../../fonts/fonts';
import { D20 } from '../dice/D20';
import { CardDimensions } from '../../../../cards/playerCards/CardDimensions';

// Re-usable component, now with styles linked to dimensions
const ResultingScore = (props: {
  score: number;
  colours: Colours;
  dimensions: CardDimensions;
}) => {
  const { score, colours, dimensions } = props;
  return (
    <Typography
      variant="h4"
      component="div"
      sx={{
        fontSize: dimensions.playerNameFontSize,
        fontFamily: IMPACT_FONTS,
        width: '100%',
        // Use a calc() to make the border slightly thicker but still relative
        borderTop: `calc(${dimensions.borderWidth} * 2) solid ${colours.primaryTextColour}`,
        borderBottom: `calc(${dimensions.borderWidth} * 2) solid ${colours.primaryTextColour}`,
        paddingTop: dimensions.traitCardPadding,
        marginTop: dimensions.traitsListSpacing,
        textAlign: 'center',
      }}
    >
      {`${localise('challenge.playerscore.total')} ${score}`}
    </Typography>
  );
};

// ModifierChip component, now with styles linked to dimensions
const ModifierChip = (props: {
  modifier: RoundModifier;
  colours: Colours;
  dimensions: CardDimensions;
}) => {
  const { modifier, colours, dimensions } = props;
  const sign = modifier.bonus > 0 ? '+' : '';

  const chipStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: colours.primaryBackgroundColour,
    color: colours.primaryTextColour,
    padding: `calc(${dimensions.traitCardPadding} / 2) ${dimensions.traitCardPadding}`,
    borderRadius: dimensions.cardBorderRadius,
    fontSize: dimensions.playerStatFontSize,
    fontWeight: 'bold',
  };

  if (modifier.key === 'preferredOpponent' && modifier.params?.nationId?.[0]) {
    const nationId = modifier.params.nationId[0];
    return (
      <Box sx={chipStyles}>
        <Typography
          sx={{ fontSize: 'inherit', fontWeight: 'inherit', marginRight: 1 }}
        >
          {`${sign}${modifier.bonus} ${localise(`modifier.${modifier.key}`)}`}
        </Typography>
        <img
          src={`/images/flags/192x144/${nationId}.png`}
          alt={`${nationId} flag`}
          style={{ height: dimensions.playerStatFontSize, display: 'block' }}
        />
      </Box>
    );
  }

  return (
    <Typography sx={chipStyles}>
      {`${sign}${modifier.bonus} ${localise(`modifier.${modifier.key}`)}`}
    </Typography>
  );
};

interface ScoreBreakdownProps {
  player: CalculatedPlayer;
  playerScore: PlayerRoundScore;
  dimensions: CardDimensions;
}

export const ScoreBreakdown = (props: ScoreBreakdownProps) => {
  const { player, playerScore, dimensions } = props;

  const modifierChips = playerScore.modifiers
    .filter((modifier) => modifier.bonus !== 0)
    .map((modifier) => (
      <ModifierChip
        key={modifier.key}
        modifier={modifier}
        colours={player.colours}
        dimensions={dimensions}
      />
    ));

  return (
    <Card
      sx={{
        maxWidth: dimensions.cardWidth,
        backgroundColor: player.colours.primaryBackgroundColour,
        color: player.colours.primaryTextColour,
        padding: dimensions.playerNameBackgroundPadding, // Use dimensions for padding
      }}
    >
      <Stack direction="column" spacing={dimensions.traitsListSpacing}>
        {' '}
        {/* Use dimensions for spacing */}
        {/* 1. Prominent D20 Roll */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <D20
            roll={false}
            result={playerScore.roll}
            colours={player.colours}
            fontSize={dimensions.playerNameFontSize} // Use dimensions for font size
          />
        </Box>
        {/* 2. Inline, wrapping modifiers */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: dimensions.traitsListSpacing, // Use dimensions for gap
            justifyContent: 'center',
            paddingY: dimensions.traitCardPadding, // Use Y-axis padding from dimensions
            borderTop: `${dimensions.attributeBubbleBorderWidth} dashed ${player.colours.primaryTextColour}`,
            borderBottom: `${dimensions.attributeBubbleBorderWidth} dashed ${player.colours.primaryTextColour}`,
          }}
        >
          {modifierChips}
        </Box>
        {/* 3. Final Result */}
        <ResultingScore
          score={playerScore.score}
          colours={player.colours}
          dimensions={dimensions}
        />
      </Stack>
    </Card>
  );
};
